import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ShieldCheck, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const AdminLoginScreen = () => {
  const navigate = useNavigate();
  const { loginAsAdmin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const res = await loginAsAdmin(email.trim(), password);
    setSubmitting(false);
    if (res.success) {
      navigate('/admin', { replace: true });
    } else {
      setError(res.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4" data-testid="admin-login-screen">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#1B2A7B] flex items-center justify-center shadow-lg shadow-[#1B2A7B]/20">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A7B] mt-4">Internal Admin</h1>
          <p className="text-sm text-[#1B2A7B]/90 font-medium tracking-wide mt-1">BFR · Operations Console</p>
          <p className="text-xs text-slate-500 mt-2 text-center">Read-only access for the Superhost team.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@getroombookings.com"
              required
              data-testid="admin-login-email"
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm placeholder-slate-500 focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B] outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="admin-login-password"
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm placeholder-slate-500 focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B] outline-none transition-colors pr-10"
              />
              <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700" data-testid="admin-login-toggle-password">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#1B2A7B] text-white font-semibold text-sm disabled:opacity-60 hover:bg-[#152163] transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Sign in
          </button>
        </form>

        <Link to="/login" className="flex items-center justify-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm mt-5" data-testid="admin-login-back-to-owner">
          <ArrowLeft className="w-4 h-4" /> Back to Property Owner login
        </Link>
      </div>
    </div>
  );
};

export default AdminLoginScreen;
